import { ReactNode } from 'react';

import { BoxProps, Box } from '@mui/material';
import clsx from 'clsx';

import { Inset, InsetProps } from './Inset';
import { mergeSx } from '../../utils';
import { classNamesFor } from '../../utils/classNames';

const classNamesFactory = classNamesFor('RowInset');

const classNames = {
  root: classNamesFactory.root(),
  startContentWrapper: classNamesFactory.element('StartContentWrapper'),
  endContentWrapper: classNamesFactory.element('EndContentWrapper'),
};

export type RowInsetProps = {
  /** Content to be displayed at the start of the row */
  startContent?: ReactNode;
  /** Content to be displayed at the end of the row */
  endContent?: ReactNode;
  /** Props forwarded to slot wrapper elements */
  slotProps?: {
    startContentWrapper?: BoxProps;
    endContentWrapper?: BoxProps;
  };
} & InsetProps;

/**
 * Inset that displays content in a non-wrapping row and has startContent and endContent
 */
export const RowInset = ({
  startContent,
  endContent,
  slotProps,
  className,
  sx,
  ...rest
}: RowInsetProps) => {
  return (
    <Inset
      className={clsx(classNames.root, className)}
      sx={mergeSx(
        {
          display: 'flex',
          flexWrap: 'nowrap',
          justifyContent: (() => {
            if (startContent && endContent) {
              return 'space-between';
            }
            if (startContent) {
              return 'flex-start';
            }
            if (endContent) {
              return 'flex-end';
            }
            return undefined;
          })(),
        },
        sx
      )}
      {...rest}
    >
      {!!startContent && (
        <Box
          data-testid="start-content"
          {...slotProps?.startContentWrapper}
          className={clsx(
            classNames.startContentWrapper,
            slotProps?.startContentWrapper?.className
          )}
        >
          {startContent}
        </Box>
      )}
      {!!endContent && (
        <Box
          data-testid="end-content"
          {...slotProps?.endContentWrapper}
          className={clsx(
            classNames.endContentWrapper,
            slotProps?.endContentWrapper?.className
          )}
        >
          {endContent}
        </Box>
      )}
    </Inset>
  );
};

RowInset.classNames = classNames;
